import React, { useEffect, useState } from "react";

import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    IconButton,
    Autocomplete,
    Checkbox,
    ListItemText,
    Divider
} from "@mui/material";
import { ThemeProvider } from "../v2/home/theme/ThemeProvider";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import Grid from "@mui/material/Grid2";
import AxiosInstance from "../../services/AxiosInstance";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Tooltip from "@mui/material/Tooltip";

import {
    CONFIGURATION_ITEM_URL,
    CONFIGURATION_CATEGORY_URL,
    CONFIGURATION_ENVIRONMENT_URL,
    RESOLVER_GROUP_URL,
    USERS_URL,
    ORG_URL,
    ASSET_ITEM_URL,
    IMPACTS_URL
} from "../../Constants/Constants";

import { useAlert } from "../alerts/AlertContext";


const ConfigurationItemDialog = ({
    configurationItemId = "",
    configurationItemName = "Add Configuration Item",
    variant = "button",
    onSuccess
}) => {

    const showAlert = useAlert();

    const [open, setOpen] = useState(false);

    const [loading, setLoading] =
        useState(false);

    const [saving, setSaving] =
        useState(false);

    const [loadingMasters, setLoadingMasters] =
        useState(false);

    const [viewMode, setViewMode] =
        useState(false);


    /*
     * Form data
     */
    const [formData, setFormData] = useState({
        name: "",
        configurationCategoryId: "",
        description: "",
        configurationEnvironmentId: "",
        impactId: "",
        owner: "",
        organizationId: "",
        configurationResolverGroupsIds: [],
        assetIds: []
    });


    /*
     * Master data
     */
    const [categories, setCategories] =
        useState([]);

    const [environments, setEnvironments] =
        useState([]);

    const [resolverGroups, setResolverGroups] =
        useState([]);

    const [users, setUsers] =
        useState([]);

    const [organizations, setOrganizations] =
        useState([]);

    const [assets, setAssets] =
        useState([]);

    const [criticalityOptions, setCriticalityOptions] =
        useState([]);

    /*
     * Linked asset popup
     */
    const [assetDialogOpen, setAssetDialogOpen] =
        useState(false);

    const [selectedAssets, setSelectedAssets] =
        useState([]);


    /*
     * Criticality
     */
    // const criticalityOptions = [
    //     {
    //         id: 1,
    //         name: "High"
    //     },
    //     {
    //         id: 2,
    //         name: "Medium"
    //     },
    //     {
    //         id: 3,
    //         name: "Low"
    //     }
    // ];


    /*
     * Load master data
     */
    const loadMasters = async () => {

        setLoadingMasters(true);

        try {

            const [
                categoryResponse,
                environmentResponse,
                resolverGroupResponse,
                organizationResponse,
                assetResponse,
                impactResponse
            ] = await Promise.all([

                AxiosInstance.get(
                    CONFIGURATION_CATEGORY_URL
                ),

                AxiosInstance.get(
                    CONFIGURATION_ENVIRONMENT_URL
                ),

                AxiosInstance.get(
                    RESOLVER_GROUP_URL
                ),

                AxiosInstance.get(
                    ORG_URL
                ),

                AxiosInstance.get(
                    ASSET_ITEM_URL
                ),

                AxiosInstance.get(
                    IMPACTS_URL
                )

            ]);


            setCategories(
                categoryResponse.data || []
            );

            setEnvironments(
                environmentResponse.data || []
            );

            setResolverGroups(
                resolverGroupResponse.data.data || []
            );

            setOrganizations(
                organizationResponse.data.data || []
            );

            setAssets(
                assetResponse.data.data || []
            );

            setCriticalityOptions(
                Array.isArray(impactResponse.data)
                    ? impactResponse.data
                    : impactResponse.data?.data || []
            );


        } catch (error) {

            console.error(
                "Unable to load configuration masters",
                error
            );

            showAlert(
                "Unable to load configuration master data",
                "error"
            );

        } finally {
            setLoadingMasters(false);
        }
    };


    /*
     * Load users
     */
    const searchUsers = async (searchValue) => {

        if (
            !searchValue ||
            searchValue.trim().length < 2
        ) {
            setUsers([]);
            return;
        }

        try {

            const response =
                await AxiosInstance.get(
                    `${USERS_URL}/search`,
                    {
                        params: {
                            search:
                                searchValue.trim()
                        }
                    }
                );

            setUsers(
                response.data || []
            );

        } catch (error) {

            console.error(
                "Unable to search users",
                error
            );

            setUsers([]);
        }
    };


    /*
     * Open dialog
     */
    const handleClickOpen = async () => {
        setViewMode(false);

        if (configurationItemId && configurationItemId !== "") {
            setLoading(true);

            try {
                const response = await AxiosInstance.get(
                    `${CONFIGURATION_ITEM_URL}/${configurationItemId}`
                );

                const item = response.data?.data || response.data;

                console.log("Configuration Item response:", item);

                /*
                 * -----------------------------
                 * RESOLVER GROUP IDS
                 * -----------------------------
                 */
                const resolverGroupIds =
                    item.configurationResolverGroupsIds ||
                    item.configurationResolverGroupIds ||
                    item.resolverGroupIds ||
                    [];

                const normalizedResolverGroupIds =
                    Array.isArray(resolverGroupIds)
                        ? resolverGroupIds.map(id => String(id))
                        : resolverGroupIds
                            ? [String(resolverGroupIds)]
                            : [];


                /*
                 * -----------------------------
                 * ASSET IDS
                 * -----------------------------
                 */
                const assetIds =
                    item.assetIds ||
                    item.configurationItemAssetIds ||
                    [];

                const normalizedAssetIds =
                    Array.isArray(assetIds)
                        ? assetIds.map(id => String(id))
                        : assetIds
                            ? [String(assetIds)]
                            : [];


                /*
                 * -----------------------------
                 * FORM DATA
                 * -----------------------------
                 */
                setFormData({
                    name: item.name || "",

                    configurationCategoryId:
                        item.configurationCategoryId ?? "",

                    description:
                        item.description || "",

                    configurationEnvironmentId:
                        item.configurationEnvironmentId ?? "",

                    impactId:
                        item.impactId ?? "",

                    owner:
                        item.owner != null
                            ? String(item.owner)
                            : "",

                    organizationId:
                        item.organizationId != null
                            ? String(item.organizationId)
                            : "",

                    configurationResolverGroupsIds:
                        normalizedResolverGroupIds,

                    assetIds:
                        normalizedAssetIds
                });


                /*
                 * -----------------------------
                 * OWNER
                 * -----------------------------
                 *
                 * Backend should preferably return:
                 *
                 * ownerDetails: {
                 *    id: "...",
                 *    name: "..."
                 * }
                 *
                 * Support both ownerDetails and
                 * ownerName responses.
                 */
                let ownerObject = null;

                if (item.ownerDetails) {

                    ownerObject = {
                        id: item.ownerDetails.id ?? item.owner,
                        name: item.ownerDetails.name || ""
                    };

                } else if (item.ownerName && item.owner) {

                    ownerObject = {
                        id: item.owner,
                        name: item.ownerName
                    };

                } else if (
                    item.owner &&
                    typeof item.owner === "object"
                ) {

                    ownerObject = {
                        id: item.owner.id,
                        name: item.owner.name || ""
                    };
                }

                if (ownerObject) {
                    setUsers([ownerObject]);
                } else {
                    setUsers([]);
                }


                /*
                 * -----------------------------
                 * LINKED ASSETS
                 * -----------------------------
                 *
                 * Backend should preferably return:
                 *
                 * linkedAssets: [
                 *   {
                 *      id,
                 *      name,
                 *      assetTag,
                 *      assetType
                 *   }
                 * ]
                 */
                let linkedAssets =
                    item.linkedAssets ||
                    item.assets ||
                    [];

                if (!Array.isArray(linkedAssets)) {
                    linkedAssets = [];
                }

                const normalizedAssets =
                    linkedAssets.map(asset => ({
                        id: asset.id,
                        name: asset.name || "",
                        assetTag: asset.assetTag || "",
                        assetType:
                            asset.assetType || null
                    }));

                setSelectedAssets(normalizedAssets);


                /*
                 * Open dialog
                 */
                setOpen(true);

            } catch (error) {

                console.error(
                    "Unable to load configuration item",
                    error
                );

                showAlert(
                    "Unable to load Configuration Item",
                    "error"
                );

            } finally {
                setLoading(false);
            }

        } else {

            /*
             * -----------------------------
             * ADD MODE
             * -----------------------------
             */
            setFormData({
                name: "",
                configurationCategoryId: "",
                description: "",
                configurationEnvironmentId: "",
                impactId: "",
                owner: "",
                organizationId: "",
                configurationResolverGroupsIds: [],
                assetIds: []
            });

            setSelectedAssets([]);
            setUsers([]);

            setOpen(true);
        }
    };


    /*
     * Load masters when dialog opens
     */
    useEffect(() => {

        if (open) {
            loadMasters();
        }

    }, [open]);


    /*
     * Handle normal fields
     */
    const handleChange = (event) => {

        const {
            name,
            value
        } = event.target;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };


    /*
     * Owner selection
     */
    const handleOwnerChange = (
        event,
        newValue
    ) => {

        setFormData(prev => ({
            ...prev,
            owner:
                newValue?.id || ""
        }));
    };


    /*
     * Resolver groups
     */
    const handleResolverGroupChange = (event) => {
        const value = event.target.value;

        const selectedIds = Array.isArray(value)
            ? value.map(id => String(id))
            : [];

        setFormData(prev => ({
            ...prev,
            configurationResolverGroupsIds: selectedIds
        }));
    };


    /*
     * Asset selection
     */
    const handleAssetSelection = (
        event,
        asset
    ) => {

        const assetId =
            asset.id;

        setSelectedAssets(prev => {

            const exists =
                prev.some(
                    item =>
                        String(item.id) ===
                        String(assetId)
                );

            if (exists) {

                return prev.filter(
                    item =>
                        String(item.id) !==
                        String(assetId)
                );
            }

            return [
                ...prev,
                asset
            ];
        });
    };


    /*
     * Remove selected asset
     */
    const removeAsset = (assetId) => {

        setSelectedAssets(prev =>
            prev.filter(
                asset =>
                    String(asset.id) !==
                    String(assetId)
            )
        );
    };

    const handleRemoveAsset = (assetId) => {
        setSelectedAssets((prev) =>
            prev.filter(
                (asset) =>
                    String(asset.id) !== String(assetId)
            )
        );
    };

    /*
     * Validation
     */
    const validate = () => {

        if (!formData.name.trim()) {

            showAlert(
                "CI Name is required",
                "error"
            );

            return false;
        }


        if (!formData.configurationCategoryId) {

            showAlert(
                "CI Category is required",
                "error"
            );

            return false;
        }


        if (!formData.configurationEnvironmentId) {

            showAlert(
                "Environment is required",
                "error"
            );

            return false;
        }


        if (!formData.impactId) {

            showAlert(
                "Criticality is required",
                "error"
            );

            return false;
        }


        if (!formData.owner) {

            showAlert(
                "CI Owner is required",
                "error"
            );

            return false;
        }


        if (!formData.configurationResolverGroupsIds.length) {

            showAlert(
                "Select at least one Resolver Group",
                "error"
            );

            return false;
        }


        return true;
    };


    /*
     * Save
     */
    const handleSubmit = async () => {

        if (!validate()) {
            return;
        }

        setSaving(true);

        try {

            const payload = {

                name:
                    formData.name.trim(),

                configurationCategoryId:
                    Number(
                        formData.configurationCategoryId
                    ),

                description:
                    formData.description.trim(),

                configurationEnvironmentId:
                    Number(
                        formData.configurationEnvironmentId
                    ),

                impactId:
                    Number(
                        formData.impactId
                    ),

                owner:
                    String(
                        formData.owner
                    ),

                organizationId:
                    formData.organizationId
                        ? Number(
                            formData.organizationId
                        )
                        : null,

                configurationResolverGroupsIds:
                    formData.configurationResolverGroupsIds,

                assetIds:
                    selectedAssets.map(
                        asset =>
                            asset.id
                    )
            };


            let response;


            if (
                configurationItemId
            ) {

                response =
                    await AxiosInstance.put(
                        `${CONFIGURATION_ITEM_URL}/${configurationItemId}`,
                        payload
                    );

            } else {

                response =
                    await AxiosInstance.post(
                        CONFIGURATION_ITEM_URL,
                        payload
                    );
            }


            showAlert(
                configurationItemId
                    ? "Configuration Item updated successfully"
                    : "Configuration Item created successfully",
                "success"
            );


            onSuccess?.(
                response.data
            );

            handleClose();

        } catch (error) {

            console.error(
                "Unable to save configuration item",
                error
            );

            showAlert(
                error?.response?.data?.message ||
                "Unable to save Configuration Item",
                "error"
            );

        } finally {

            setSaving(false);
        }
    };


    /*
     * Close
     */
    const handleClose = () => {

        if (saving) {
            return;
        }

        setOpen(false);

        setSelectedAssets([]);

        setUsers([]);

        setViewMode(false);
    };


    /*
     * Selected owner
     */
    const selectedOwner =
        users.find(
            user =>
                String(user.id) ===
                String(formData.owner)
        ) || null;


    /*
     * Selected Resolver groups
     */
    const selectedResolverGroups =
        resolverGroups.filter(
            group =>
                formData.configurationResolverGroupsIds
                    .map(String)
                    .includes(
                        String(group.id)
                    )
        );


    /*
     * Trigger
     */
    const renderTrigger = () => {

        if (
            variant === "link" &&
            configurationItemId
        ) {

            return (

                <Button
                    variant="text"
                    onClick={handleClickOpen}
                    disabled={loading}
                >
                    {configurationItemName || "View"}
                </Button>

            );
        }


        return (

            <Button
                variant="contained"
                startIcon={
                    <AddOutlinedIcon />
                }
                onClick={handleClickOpen}
                disabled={loading}
            >
                {configurationItemName ||
                    "Add Configuration Item"}
            </Button>

        );
    };


    return (
        <>
            <ThemeProvider>
                {renderTrigger()}


                <Dialog
                    open={open}
                    onClose={
                        saving
                            ? undefined
                            : handleClose
                    }
                    fullWidth
                    maxWidth="md"
                >

                    <DialogTitle
                        sx={{
                            display: "flex",
                            justifyContent:
                                "space-between",
                            alignItems: "center"
                        }}
                    >

                        <Typography
                            variant="h6"
                            fontWeight={600}
                        >
                            {configurationItemId
                                ? "Configuration Item"
                                : "Add Configuration Item"}
                        </Typography>


                        <IconButton
                            onClick={handleClose}
                            disabled={saving}
                        >
                            <CloseIcon />
                        </IconButton>

                    </DialogTitle>


                    <DialogContent
                        dividers
                    >

                        {loadingMasters ? (

                            <Box
                                sx={{
                                    minHeight: 300,
                                    display: "flex",
                                    justifyContent:
                                        "center",
                                    alignItems:
                                        "center"
                                }}
                            >

                                <CircularProgress />

                            </Box>

                        ) : (

                            <Stack
                                spacing={3}
                            >

                                {/* =========================
                                BASIC INFORMATION
                            ========================== */}

                                <Box>

                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={600}
                                        sx={{
                                            mb: 2
                                        }}
                                    >
                                        Basic Information
                                    </Typography>


                                    <Stack
                                        spacing={2}
                                    >

                                        <Grid container spacing={2}>
                                            {/* Row 1 */}
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <TextField
                                                    fullWidth
                                                    label="CI Name"
                                                    name="name"
                                                    value={
                                                        formData.name
                                                    }
                                                    onChange={
                                                        handleChange
                                                    }
                                                    disabled={
                                                        viewMode
                                                    }
                                                    required
                                                />

                                            </Grid>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <FormControl
                                                    fullWidth
                                                    required
                                                >

                                                    <InputLabel>
                                                        CI Category
                                                    </InputLabel>

                                                    <Select
                                                        name="configurationCategoryId"
                                                        value={
                                                            formData.configurationCategoryId
                                                        }
                                                        label="CI Category"
                                                        onChange={
                                                            handleChange
                                                        }
                                                        disabled={
                                                            viewMode
                                                        }
                                                    >

                                                        {categories.map(
                                                            category => (

                                                                <MenuItem
                                                                    key={
                                                                        category.id
                                                                    }
                                                                    value={
                                                                        category.id
                                                                    }
                                                                >
                                                                    {
                                                                        category.name
                                                                    }
                                                                </MenuItem>

                                                            )
                                                        )}

                                                    </Select>

                                                </FormControl>

                                            </Grid>
                                        </Grid>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={3}
                                            label="Description"
                                            name="description"
                                            value={
                                                formData.description
                                            }
                                            onChange={
                                                handleChange
                                            }
                                            disabled={
                                                viewMode
                                            }
                                        />

                                        <Grid container spacing={2}>
                                            {/* Row 1 */}
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <FormControl
                                                    fullWidth
                                                    required
                                                >

                                                    <InputLabel>
                                                        Environment
                                                    </InputLabel>

                                                    <Select
                                                        name="configurationEnvironmentId"
                                                        value={
                                                            formData.configurationEnvironmentId
                                                        }
                                                        label="Environment"
                                                        onChange={
                                                            handleChange
                                                        }
                                                        disabled={
                                                            viewMode
                                                        }
                                                    >

                                                        {environments.map(
                                                            environment => (

                                                                <MenuItem
                                                                    key={
                                                                        environment.id
                                                                    }
                                                                    value={
                                                                        environment.id
                                                                    }
                                                                >
                                                                    {
                                                                        environment.name
                                                                    }
                                                                </MenuItem>

                                                            )
                                                        )}

                                                    </Select>

                                                </FormControl>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 6 }}>

                                                {/* Criticality */}
                                                <Box>

                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            mb: 1
                                                        }}
                                                    >
                                                        Criticality
                                                    </Typography>


                                                    <Stack
                                                        direction="row"
                                                        spacing={1}
                                                    >

                                                        {criticalityOptions.map(
                                                            option => (

                                                                <Button
                                                                    key={
                                                                        option.id
                                                                    }
                                                                    variant={
                                                                        String(
                                                                            formData.impactId
                                                                        ) ===
                                                                            String(
                                                                                option.id
                                                                            )
                                                                            ? "contained"
                                                                            : "outlined"
                                                                    }
                                                                    onClick={() =>
                                                                        setFormData(
                                                                            prev => ({
                                                                                ...prev,
                                                                                impactId:
                                                                                    option.id
                                                                            })
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        viewMode
                                                                    }
                                                                >
                                                                    {
                                                                        option.name
                                                                    }
                                                                </Button>

                                                            )
                                                        )}

                                                    </Stack>

                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Stack>

                                </Box>


                                <Divider />


                                {/* =========================
                                OWNERSHIP
                            ========================== */}

                                <Box>

                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={600}
                                        sx={{
                                            mb: 2
                                        }}
                                    >
                                        Ownership
                                    </Typography>


                                    <Stack
                                        spacing={2}
                                    >

                                        <Grid container spacing={2}>
                                            {/* Row 1 */}
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                {/* Owner */}

                                                <Autocomplete
                                                    fullWidth
                                                    options={Array.isArray(users) ? users : []}
                                                    value={selectedOwner}
                                                    disabled={viewMode}
                                                    isOptionEqualToValue={(option, selected) =>
                                                        String(option?.id) === String(selected?.id)
                                                    }
                                                    getOptionLabel={(option) =>
                                                        option?.name || ""
                                                    }
                                                    onChange={handleOwnerChange}
                                                    onInputChange={(
                                                        event,
                                                        newValue,
                                                        reason
                                                    ) => {

                                                        if (reason === "input") {
                                                            searchUsers(newValue);
                                                        }
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="CI Owner"
                                                            required
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 6 }}>

                                                {/* Organization */}

                                                <FormControl
                                                    fullWidth
                                                >

                                                    <InputLabel>
                                                        Organization
                                                    </InputLabel>

                                                    <Select
                                                        name="organizationId"
                                                        value={
                                                            formData.organizationId
                                                        }
                                                        label="Organization"
                                                        onChange={
                                                            handleChange
                                                        }
                                                        disabled={
                                                            viewMode
                                                        }
                                                    >

                                                        {organizations.map(
                                                            organization => (

                                                                <MenuItem
                                                                    key={
                                                                        organization.id
                                                                    }
                                                                    value={
                                                                        organization.id
                                                                    }
                                                                >
                                                                    {
                                                                        organization.name
                                                                    }
                                                                </MenuItem>

                                                            )
                                                        )}

                                                    </Select>

                                                </FormControl>

                                            </Grid>
                                        </Grid>

                                        {/* Resolver Groups */}

                                        <FormControl fullWidth required>
                                            <InputLabel>Resolver Groups</InputLabel>

                                            <Select
                                                multiple
                                                name="configurationResolverGroupsIds"
                                                value={formData.configurationResolverGroupsIds || []}
                                                label="Resolver Groups"
                                                onChange={handleResolverGroupChange}
                                                disabled={viewMode}
                                                renderValue={(selected) => (
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            gap: 0.5,
                                                            flexWrap: "wrap"
                                                        }}
                                                    >
                                                        {selected.map((id) => {
                                                            const group = resolverGroups.find(
                                                                group =>
                                                                    String(group.id) === String(id)
                                                            );

                                                            return (
                                                                <Chip
                                                                    key={String(id)}
                                                                    label={group?.name || id}
                                                                    size="small"
                                                                />
                                                            );
                                                        })}
                                                    </Box>
                                                )}
                                            >
                                                {resolverGroups.map((group) => {

                                                    const checked =
                                                        (formData.configurationResolverGroupsIds || [])
                                                            .map(String)
                                                            .includes(String(group.id));

                                                    return (
                                                        <MenuItem
                                                            key={group.id}
                                                            value={String(group.id)}
                                                        >
                                                            <Checkbox checked={checked} />

                                                            <ListItemText
                                                                primary={group.name}
                                                            />
                                                        </MenuItem>
                                                    );
                                                })}
                                            </Select>
                                        </FormControl>

                                    </Stack>

                                </Box>


                                <Divider />


                                {/* =========================
                                LINK ASSETS
                            ========================== */}

                                <Box>

                                    <Box
                                        sx={{
                                            display:
                                                "flex",
                                            justifyContent:
                                                "space-between",
                                            alignItems:
                                                "center",
                                            mb: 2
                                        }}
                                    >

                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={600}
                                        >
                                            Linked Assets
                                        </Typography>


                                        <Button
                                            variant="outlined"
                                            startIcon={
                                                <LinkIcon />
                                            }
                                            onClick={() =>
                                                setAssetDialogOpen(
                                                    true
                                                )
                                            }
                                            disabled={
                                                viewMode
                                            }
                                        >
                                            Link Assets
                                        </Button>

                                    </Box>


                                    {selectedAssets.length === 0 ? (

                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            No assets linked.
                                        </Typography>

                                    ) : (

                                        <Box sx={{ mt: 2 }}>
                                            {selectedAssets?.length > 0 && (
                                                <Box
                                                    sx={{
                                                        border: "1px solid",
                                                        borderColor: "divider",
                                                        borderRadius: 1,
                                                        overflow: "hidden",
                                                    }}
                                                >
                                                    {selectedAssets.map((asset, index) => (
                                                        <Box
                                                            key={asset.id}
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "space-between",
                                                                px: 2,
                                                                py: 1,
                                                                borderBottom:
                                                                    index !== selectedAssets.length - 1
                                                                        ? "1px solid"
                                                                        : "none",
                                                                borderColor: "divider",
                                                                "&:hover": {
                                                                    backgroundColor: "action.hover",
                                                                },
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: "flex",
                                                                    flexDirection: "column",
                                                                    minWidth: 0,
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    fontWeight={600}
                                                                >
                                                                    {asset.name || ""}
                                                                </Typography>

                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                >
                                                                    Asset Tag: {asset.assetTag || "-"}
                                                                    {" | "}
                                                                    Asset Type: {asset.assetType.name || "-"}
                                                                </Typography>
                                                            </Box>

                                                            <Tooltip title="Remove Asset">
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() =>
                                                                        handleRemoveAsset(asset.id)
                                                                    }
                                                                >
                                                                    <DeleteOutlineIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}
                                        </Box>

                                    )}

                                </Box>

                            </Stack>
                        )}

                    </DialogContent>


                    <DialogActions
                        sx={{
                            px: 3,
                            py: 2
                        }}
                    >

                        <Button
                            onClick={handleClose}
                            disabled={saving}
                        >
                            Close
                        </Button>


                        {!viewMode && (

                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={
                                    saving ||
                                    loadingMasters
                                }
                                startIcon={
                                    saving
                                        ? <CircularProgress
                                            size={18}
                                        />
                                        : null
                                }
                            >
                                {saving
                                    ? "Saving..."
                                    : configurationItemId
                                        ? "Update"
                                        : "Save"}
                            </Button>

                        )}

                    </DialogActions>

                </Dialog>


                {/* ======================================
                ASSET SELECTION POPUP
            ======================================= */}

                <Dialog
                    open={assetDialogOpen}
                    onClose={() =>
                        setAssetDialogOpen(false)
                    }
                    fullWidth
                    maxWidth="md"
                >

                    <DialogTitle>
                        Link Assets
                    </DialogTitle>


                    <DialogContent
                        dividers
                    >

                        <Autocomplete
                            multiple
                            fullWidth
                            options={Array.isArray(assets) ? assets : []}
                            value={selectedAssets || []}
                            disableCloseOnSelect
                            loading={loadingMasters}
                            isOptionEqualToValue={(option, value) =>
                                String(option.id) === String(value.id)
                            }
                            getOptionLabel={(option) =>
                                option?.name || ""
                            }
                            onChange={(event, newValue) => {
                                setSelectedAssets(newValue);
                            }}
                            renderOption={(props, option, { selected }) => (
                                <li {...props} key={option.id}>
                                    <Box
                                        sx={{
                                            width: "100%",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            border: "1px solid",
                                            borderColor: selected
                                                ? "primary.main"
                                                : "divider",
                                            borderRadius: 1,
                                            px: 1,
                                            py: 1,
                                            mb: 0.5,
                                            backgroundColor: selected
                                                ? "action.selected"
                                                : "background.paper",
                                        }}
                                    >
                                        <Checkbox
                                            checked={selected}
                                            size="small"
                                            sx={{
                                                p: 0.5,
                                            }}
                                        />

                                        <Box
                                            sx={{
                                                flex: 1,
                                                minWidth: 0,
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                fontWeight={600}
                                            >
                                                {option.name || "-"}
                                            </Typography>

                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                Asset Tag: {option.assetTag || "-"}
                                                {" | "}
                                                Asset Type:{" "}
                                                {option.assetType?.name ||
                                                    option.assetType ||
                                                    "-"}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Link Assets"
                                    placeholder="Search assets..."
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingMasters && (
                                                    <CircularProgress
                                                        size={20}
                                                    />
                                                )}

                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />

                    </DialogContent>


                    <DialogActions>

                        <Button
                            onClick={() =>
                                setAssetDialogOpen(
                                    false
                                )
                            }
                        >
                            Done
                        </Button>

                    </DialogActions>

                </Dialog>
            </ThemeProvider>
        </>
    );
};


export default ConfigurationItemDialog;